import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Download, Pause, Play } from 'lucide-react'

import { LIVE_OVERLAP_STYLE, shortLabel, SpeakerStyleMap, styleForAttribution } from './colors'
import { liveOverlaps } from './liveOverlaps'

import styles from './Swimlane.module.scss'

import Button from '@/components/Button'
import type { AttributionSpan } from '@/pages/app/AttributionViewer/artifactDerivations'

export type LaneSpec = {
  id: string
  label: string
  state: 'loading' | 'present' | 'absent' | 'error'
  spans: AttributionSpan[]
  emptyMessage?: string
  subtracks?: { label: string; spans: AttributionSpan[] }[]
  // Names the comparison baseline in the label of a changed span.
  counterpartLabel?: string
  // A muted second line under the lane name.
  note?: string
  // A collapsed lane under this one, revealed by a chevron on the lane name.
  child?: LaneSpec
  disclosure?: { isExpanded: boolean; label: string; controls: string; onToggle: () => void }
  elementId?: string
}

export type ViewportChange = {
  from: number
  to: number
  replace?: boolean
}

export type AudioLaneSpec = {
  state: 'idle' | 'loading' | 'present' | 'absent' | 'error'
  durationMs: number | null
  isDownloading: boolean
  isPlaying: boolean
  onDownload: () => void
  onLoad: () => void
  onRetry: () => void
  onTogglePlayback: () => void
}

type SwimlaneProps = {
  audioLane: AudioLaneSpec
  lanes: LaneSpec[]
  timelineStart: number
  timelineEnd: number
  viewStart: number
  viewEnd: number
  speakerStyles: SpeakerStyleMap
  hoverTime: number | null
  playheadTime: number | null
  pinnedTime: number | null
  selectedRange?: { startMs: number; endMs: number } | null
  onNavigateChunk?: (direction: -1 | 1) => void
  onHoverTimeChange: (t: number | null) => void
  onPinnedTimeChange: (t: number | null) => void
  onViewChange: (change: ViewportChange) => void
  onResetZoom?: () => void
}

type DragState = {
  pointerId: number
  startPx: number
  trackWidth: number
  viewEnd: number
  viewStart: number
}

type PlayheadDragState = {
  pointerId: number
}

type TrackMetrics = {
  left: number
  width: number
}

const timeFromDragPx = (px: number, drag: DragState): number => {
  const dragDuration = Math.max(1, drag.viewEnd - drag.viewStart)
  return Math.round(drag.viewStart + (px / drag.trackWidth) * dragDuration)
}

const TICK_COUNT = 6
const GUTTER_PX = 142 // Coupled to $gutter in Swimlane.module.scss for pointer-to-time math.
const DRAG_THRESHOLD_PX = 4
const PINCH_ZOOM_SENSITIVITY = 0.006
export const MIN_TIMELINE_VIEW_MS = 1_000

const barLabel = (base: string, span: AttributionSpan, counterpartLabel: string | undefined): string => {
  if (span.method) base = `${base} · ${span.method}`
  if (!span.changed) return base
  return span.detail ? `${base} (changed; ${counterpartLabel ?? 'Original'}: ${span.detail})` : `${base} (changed)`
}

const laneStateMessage = (state: LaneSpec['state'], emptyMessage?: string): string => {
  if (state === 'loading') return 'Loading artifact…'
  if (state === 'absent') return emptyMessage ?? 'No data for this meeting'
  if (state === 'error') return 'Artifact could not be read'
  return ''
}

const SpanBars = ({
  spans,
  viewStart,
  viewEnd,
  speakerStyles,
  counterpartLabel,
}: {
  spans: AttributionSpan[]
  viewStart: number
  viewEnd: number
  speakerStyles: SpeakerStyleMap
  counterpartLabel?: string
}) =>
  spans.map((span, index) => {
    const start = Math.max(span.start_ms, viewStart)
    const end = Math.min(span.end_ms, viewEnd)
    const isPoint = span.start_ms === span.end_ms
    if (isPoint ? span.start_ms < viewStart || span.start_ms >= viewEnd : end <= viewStart || start >= viewEnd)
      return null
    const speakerStyle = styleForAttribution(span, speakerStyles)
    return (
      <div
        key={`${span.start_ms}:${index}`}
        className={styles.bar}
        data-pattern={speakerStyle.pattern}
        data-point={isPoint ? 'true' : undefined}
        title={
          isPoint
            ? `${barLabel(shortLabel(speakerStyle), span, counterpartLabel)} · ${formatMs(span.start_ms)} (timestamp only)`
            : undefined
        }
        data-changed={span.changed ? 'true' : undefined}
        role="img"
        aria-label={barLabel(shortLabel(speakerStyle), span, counterpartLabel)}
        style={{
          left: `${((start - viewStart) / (viewEnd - viewStart)) * 100}%`,
          width: isPoint ? '4px' : `${Math.max(0.15, ((end - start) / (viewEnd - viewStart)) * 100)}%`,
          backgroundColor: speakerStyle.color,
        }}
      />
    )
  })

export function packLiveSubtracks(spans: AttributionSpan[]): LaneSpec['subtracks'] {
  const tracks: { label: string; spans: AttributionSpan[] }[] = []
  for (const span of [...spans].sort((a, b) => a.start_ms - b.start_ms || a.end_ms - b.end_ms)) {
    const track = tracks.find(({ spans: packed }) => {
      const last = packed[packed.length - 1]
      return (
        last.end_ms < span.start_ms ||
        (last.end_ms === span.start_ms && last.start_ms < last.end_ms && span.start_ms < span.end_ms)
      )
    })
    if (track) track.spans.push(span)
    else tracks.push({ label: `Track ${tracks.length + 1}`, spans: [span] })
  }
  return tracks.length > 1 ? tracks : undefined
}

export type LaneSnapshot = {
  laneLabel: string
  speakers: { color: string; colorKey: string; label: string }[]
}

export function laneSnapshotsAtTime(lanes: LaneSpec[], timeMs: number, speakerStyles: SpeakerStyleMap): LaneSnapshot[] {
  return lanes.map((lane) => {
    const seen = new Set<string>()
    const speakers: LaneSnapshot['speakers'] = []
    for (const span of lane.spans) {
      if (timeMs < span.start_ms || timeMs > span.end_ms) continue
      if (lane.id === 'observations' && timeMs === span.end_ms && span.end_ms > span.start_ms) continue
      const style = styleForAttribution(span, speakerStyles)
      const key = `${style.colorKey}:${span.method ?? ''}`
      if (seen.has(key)) continue
      seen.add(key)
      speakers.push({
        color: style.color,
        colorKey: key,
        label: span.method ? `${shortLabel(style)} · ${span.method}` : shortLabel(style),
      })
    }
    return { laneLabel: lane.label, speakers }
  })
}

export function formatMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const mm = String(minutes).padStart(hours > 0 ? 2 : 1, '0')
  const ss = String(seconds).padStart(2, '0')
  return hours > 0 ? `${hours}:${mm}:${ss}` : `${mm}:${ss}`
}

const clamp = (value: number, minimum: number, maximum: number): number => Math.min(maximum, Math.max(minimum, value))

export function clampViewport(
  from: number,
  duration: number,
  timelineStart: number,
  timelineEnd: number
): ViewportChange {
  const timelineDuration = Math.max(1, timelineEnd - timelineStart)
  const boundedDuration = clamp(Math.round(duration), 1, timelineDuration)
  const boundedFrom = clamp(Math.round(from), timelineStart, timelineEnd - boundedDuration)
  return { from: boundedFrom, to: boundedFrom + boundedDuration }
}

export function panViewport(
  viewStart: number,
  viewEnd: number,
  deltaMs: number,
  timelineStart: number,
  timelineEnd: number
): ViewportChange {
  return clampViewport(viewStart + deltaMs, viewEnd - viewStart, timelineStart, timelineEnd)
}

export function zoomViewport(
  viewStart: number,
  viewEnd: number,
  anchorRatio: number,
  scale: number,
  timelineStart: number,
  timelineEnd: number
): ViewportChange {
  const timelineDuration = Math.max(1, timelineEnd - timelineStart)
  const duration = Math.max(1, viewEnd - viewStart)
  const minimumDuration = Math.min(MIN_TIMELINE_VIEW_MS, timelineDuration)
  const nextDuration = clamp(Math.round(duration * scale), minimumDuration, timelineDuration)
  const boundedRatio = clamp(anchorRatio, 0, 1)
  const anchorTime = viewStart + duration * boundedRatio
  return clampViewport(anchorTime - nextDuration * boundedRatio, nextDuration, timelineStart, timelineEnd)
}

const wheelPixels = (event: WheelEvent, value: number, pageSize: number): number => {
  if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return value * 16
  if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return value * pageSize
  return value
}

const Swimlane = ({
  audioLane,
  lanes,
  timelineStart,
  timelineEnd,
  viewStart,
  viewEnd,
  speakerStyles,
  hoverTime,
  playheadTime,
  pinnedTime,
  selectedRange = null,
  onNavigateChunk,
  onHoverTimeChange,
  onPinnedTimeChange,
  onViewChange,
  onResetZoom,
}: SwimlaneProps) => {
  const bodyRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const playheadDragRef = useRef<PlayheadDragState | null>(null)
  const wheelViewportRef = useRef({ from: viewStart, to: viewEnd })
  const [dragging, setDragging] = useState(false)
  const [playheadDragTime, setPlayheadDragTime] = useState<number | null>(null)
  const [dragCurrentPx, setDragCurrentPx] = useState<number | null>(null)
  const [expandedLaneIds, setExpandedLaneIds] = useState<ReadonlySet<string>>(() => new Set())

  const duration = Math.max(1, viewEnd - viewStart)
  const ticks = Array.from({ length: TICK_COUNT + 1 }, (_, i) => viewStart + (duration * i) / TICK_COUNT)
  const audioVisibleEnd = Math.min(viewEnd, Math.max(viewStart, audioLane.durationMs ?? viewEnd))
  const audioVisibleWidth = Math.max(0, ((audioVisibleEnd - viewStart) / duration) * 100)

  const audioRow = (
    <div className={styles.laneRow} data-lane="audio">
      <div className={styles.audioLaneLabel}>
        <span className={styles.laneName}>Audio</span>
        {audioLane.state === 'present' && (
          <div className={styles.audioControls}>
            <Button
              title={
                audioLane.isPlaying ? (
                  <Pause size={14} strokeWidth={2.2} aria-hidden="true" />
                ) : (
                  <Play size={14} strokeWidth={2.2} aria-hidden="true" />
                )
              }
              type="link"
              className={styles.audioControlButton}
              ariaLabel={audioLane.isPlaying ? 'Pause audio' : 'Play audio'}
              width={24}
              onClick={audioLane.onTogglePlayback}
            />
            <Button
              title={<Download size={14} strokeWidth={2.2} aria-hidden="true" />}
              type="link"
              className={styles.audioControlButton}
              ariaLabel="Download audio"
              isLoading={audioLane.isDownloading}
              width={24}
              onClick={audioLane.onDownload}
            />
          </div>
        )}
      </div>
      {audioLane.state === 'idle' ? (
        <button type="button" className={styles.audioTrackButton} onClick={audioLane.onLoad}>
          Click to load audio
        </button>
      ) : audioLane.state === 'loading' ? (
        <div className={styles.audioTrack} data-state="loading" role="status" aria-label="Loading audio">
          <span className={styles.audioLoadingShade} aria-hidden="true" />
          <span className={styles.audioTrackText}>Loading audio…</span>
        </div>
      ) : audioLane.state === 'error' ? (
        <button type="button" className={styles.audioTrackButton} onClick={audioLane.onRetry}>
          <span className={styles.audioTrackText}>Audio could not be loaded · Click to retry</span>
        </button>
      ) : audioLane.state === 'absent' ? (
        <div className={styles.audioTrack} data-state="absent">
          <span className={styles.audioTrackText}>No meeting audio is available</span>
        </div>
      ) : (
        <div className={styles.audioTrack} data-state="present" aria-label="Loaded audio track">
          <span className={styles.audioReadyBar} style={{ width: `${audioVisibleWidth}%` }} aria-hidden="true" />
        </div>
      )}
    </div>
  )

  const overlaps = useMemo(
    () => liveOverlaps(lanes.find((spec) => spec.id === 'live' && spec.state === 'present')?.spans ?? []),
    [lanes]
  )

  const expandedChild = useCallback(
    (spec: LaneSpec): LaneSpec | null =>
      spec.child && spec.state === 'present' && expandedLaneIds.has(spec.id) ? spec.child : null,
    [expandedLaneIds]
  )

  const visibleLanes = useMemo(
    () =>
      lanes.flatMap((spec) => {
        const child = expandedChild(spec)
        return child ? [spec, child] : [spec]
      }),
    [lanes, expandedChild]
  )

  const toggleLane = useCallback(
    (laneId: string) =>
      setExpandedLaneIds((current) => {
        const next = new Set(current)
        if (!next.delete(laneId)) next.add(laneId)
        return next
      }),
    []
  )

  const laneRows = useMemo(
    () =>
      lanes.map((lane) => {
        const liveTracks = lane.id === 'live' && lane.state === 'present' ? packLiveSubtracks(lane.spans) : undefined
        const liveExpanded = liveTracks != null && expandedLaneIds.has(lane.id)
        const spec = liveExpanded ? { ...lane, subtracks: liveTracks } : lane
        const child = expandedChild(spec)
        const childRowId = `swimlane-child-${spec.id}`
        const subtrackRowsId = `swimlane-tracks-${spec.id}`
        const disclosure =
          spec.disclosure ??
          (liveTracks
            ? {
                isExpanded: liveExpanded,
                label: `${liveExpanded ? 'Hide' : 'Show'} live tracks`,
                controls: subtrackRowsId,
                onToggle: () => toggleLane(spec.id),
              }
            : spec.child && spec.state === 'present'
              ? {
                  isExpanded: child != null,
                  label: `${child ? 'Hide' : 'Show'} ${spec.child.label.toLowerCase()} lane`,
                  controls: childRowId,
                  onToggle: () => toggleLane(spec.id),
                }
              : undefined)
        const overlapBars =
          spec.id === 'live' &&
          spec.state === 'present' &&
          overlaps.map((overlap) => {
            const start = Math.max(overlap.start, viewStart)
            const end = Math.min(overlap.end, viewEnd)
            if (end <= start) return null
            return (
              <div
                key={`overlap:${overlap.start}`}
                className={styles.bar}
                data-pattern={LIVE_OVERLAP_STYLE.pattern}
                role="img"
                aria-label={LIVE_OVERLAP_STYLE.label}
                style={{
                  left: `${((start - viewStart) / duration) * 100}%`,
                  width: `${((end - start) / duration) * 100}%`,
                  backgroundColor: LIVE_OVERLAP_STYLE.color,
                  backgroundImage: LIVE_OVERLAP_STYLE.backgroundImage,
                  pointerEvents: 'none',
                }}
              />
            )
          })
        return (
          <Fragment key={spec.id}>
            <div
              className={spec.subtracks && spec.state === 'present' ? styles.subtrackGroup : styles.laneRow}
              data-lane={spec.id}
              id={spec.elementId ?? (liveTracks ? subtrackRowsId : undefined)}
            >
              <div className={styles.laneLabel}>
                {disclosure ? (
                  <button
                    type="button"
                    className={styles.laneToggle}
                    aria-expanded={disclosure.isExpanded}
                    aria-controls={disclosure.controls}
                    aria-label={disclosure.label}
                    onClick={disclosure.onToggle}
                    // The lanes slider owns these keys; a focused toggle must not also move the cue.
                    onKeyDown={(event) => {
                      if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(event.key))
                        event.stopPropagation()
                    }}
                  >
                    {disclosure.isExpanded ? (
                      <ChevronDown size={14} strokeWidth={2.2} aria-hidden="true" />
                    ) : (
                      <ChevronRight size={14} strokeWidth={2.2} aria-hidden="true" />
                    )}
                    <span className={styles.laneName}>{spec.label}</span>
                  </button>
                ) : (
                  <span className={styles.laneName}>{spec.label}</span>
                )}
                {spec.note && <span className={styles.laneMuted}>{spec.note}</span>}
                {spec.state !== 'present' && !(spec.state === 'absent' && spec.emptyMessage) && (
                  <span className={styles.laneMuted}>{spec.state}</span>
                )}
              </div>
              {spec.subtracks && spec.state === 'present' ? (
                <>
                  {spec.subtracks.map((track) => (
                    <div
                      key={track.label}
                      className={styles.laneRow}
                      role="group"
                      aria-label={`${spec.label} · ${track.label}`}
                    >
                      <span className={styles.subtrackLabel}>{track.label}</span>
                      <div className={styles.laneTrack}>
                        <SpanBars
                          spans={track.spans}
                          viewStart={viewStart}
                          viewEnd={viewEnd}
                          speakerStyles={speakerStyles}
                        />
                      </div>
                    </div>
                  ))}
                  {spec.id === 'live' && overlaps.length > 0 && (
                    <div className={styles.laneRow} role="group" aria-label="Live overlap">
                      <span className={styles.subtrackLabel}>Mic + System</span>
                      <div className={styles.laneTrack}>{overlapBars}</div>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.laneTrack}>
                  {spec.state !== 'present' ? (
                    <div className={styles.laneAbsent}>{laneStateMessage(spec.state, spec.emptyMessage)}</div>
                  ) : (
                    <SpanBars
                      spans={spec.spans}
                      viewStart={viewStart}
                      viewEnd={viewEnd}
                      speakerStyles={speakerStyles}
                      counterpartLabel={spec.counterpartLabel}
                    />
                  )}
                  {overlapBars}
                </div>
              )}
            </div>
            {child && (
              <div
                id={childRowId}
                className={styles.laneRow}
                data-lane={child.id}
                role="group"
                aria-label={`${spec.label} · ${child.label}`}
              >
                <div className={`${styles.laneLabel} ${styles.childLaneLabel}`}>
                  <span className={styles.laneName}>{child.label}</span>
                  {child.note && <span className={styles.laneMuted}>{child.note}</span>}
                </div>
                <div className={styles.laneTrack}>
                  {child.state !== 'present' ? (
                    <div className={styles.laneAbsent}>{laneStateMessage(child.state, child.emptyMessage)}</div>
                  ) : (
                    <SpanBars
                      spans={child.spans}
                      viewStart={viewStart}
                      viewEnd={viewEnd}
                      speakerStyles={speakerStyles}
                      counterpartLabel={child.counterpartLabel}
                    />
                  )}
                </div>
              </div>
            )}
          </Fragment>
        )
      }),
    [lanes, overlaps, viewStart, viewEnd, duration, speakerStyles, expandedChild, expandedLaneIds, toggleLane]
  )

  const trackMetrics = useCallback((): TrackMetrics | null => {
    const rect = bodyRef.current?.getBoundingClientRect()
    if (!rect) return null
    return {
      left: rect.left + GUTTER_PX,
      width: Math.max(1, rect.width - GUTTER_PX),
    }
  }, [])

  const timeAtMetrics = useCallback(
    (metrics: TrackMetrics, clientX: number): number => {
      const ratio = clamp((clientX - metrics.left) / metrics.width, 0, 1)
      return Math.round(viewStart + ratio * duration)
    },
    [duration, viewStart]
  )

  const timeAtClientX = useCallback(
    (clientX: number): number => {
      const metrics = trackMetrics()
      if (!metrics) return viewStart
      return timeAtMetrics(metrics, clientX)
    },
    [timeAtMetrics, trackMetrics, viewStart]
  )

  const pxAtClientX = useCallback(
    (clientX: number): number => {
      const metrics = trackMetrics()
      if (!metrics) return 0
      return clamp(clientX - metrics.left, 0, metrics.width)
    },
    [trackMetrics]
  )

  const clearHover = useCallback(() => {
    onHoverTimeChange(null)
  }, [onHoverTimeChange])

  const hoverAtClientX = useCallback(
    (clientX: number) => {
      const metrics = trackMetrics()
      if (!metrics || clientX < metrics.left || clientX > metrics.left + metrics.width) {
        clearHover()
        return
      }
      onHoverTimeChange(timeAtMetrics(metrics, clientX))
    },
    [clearHover, onHoverTimeChange, timeAtMetrics, trackMetrics]
  )

  useEffect(() => {
    wheelViewportRef.current = { from: viewStart, to: viewEnd }
  }, [viewEnd, viewStart])

  const wheelStateRef = useRef({ clearHover, onViewChange, timelineEnd, timelineStart })
  wheelStateRef.current = { clearHover, onViewChange, timelineEnd, timelineStart }

  useEffect(() => {
    const element = bodyRef.current
    if (!element) return
    const onWheel = (event: WheelEvent) => {
      const {
        clearHover: clearWheelHover,
        onViewChange: changeView,
        timelineEnd,
        timelineStart,
      } = wheelStateRef.current
      const wheelViewport = wheelViewportRef.current
      const wheelDuration = Math.max(1, wheelViewport.to - wheelViewport.from)
      const metrics = trackMetrics()
      if (!metrics || event.clientX < metrics.left || event.clientX > metrics.left + metrics.width) return
      if (event.ctrlKey) {
        event.preventDefault()
        clearWheelHover()
        const ratio = clamp((event.clientX - metrics.left) / metrics.width, 0, 1)
        const delta = wheelPixels(event, event.deltaY, metrics.width)
        const next = zoomViewport(
          wheelViewport.from,
          wheelViewport.to,
          ratio,
          Math.exp(delta * PINCH_ZOOM_SENSITIVITY),
          timelineStart,
          timelineEnd
        )
        if (next.from !== wheelViewport.from || next.to !== wheelViewport.to) {
          wheelViewportRef.current = next
          changeView({ ...next, replace: true })
        }
        return
      }

      const horizontalDelta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.shiftKey ? event.deltaY : 0
      if (horizontalDelta === 0) return
      event.preventDefault()
      if (wheelDuration >= timelineEnd - timelineStart) return
      clearWheelHover()
      const deltaPx = wheelPixels(event, horizontalDelta, metrics.width)
      const next = panViewport(
        wheelViewport.from,
        wheelViewport.to,
        (deltaPx / metrics.width) * wheelDuration,
        timelineStart,
        timelineEnd
      )
      if (next.from !== wheelViewport.from || next.to !== wheelViewport.to) {
        wheelViewportRef.current = next
        changeView({ ...next, replace: true })
      }
    }
    element.addEventListener('wheel', onWheel, { passive: false })
    return () => element.removeEventListener('wheel', onWheel)
  }, [trackMetrics])

  // Every button under the body presses rather than cues: the idle/error audio track and the gutter controls.
  const isInteractiveControl = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest('button') != null

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || isInteractiveControl(event.target)) return
    const metrics = trackMetrics()
    if (!metrics || event.clientX < metrics.left || event.clientX > metrics.left + metrics.width) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const startPx = pxAtClientX(event.clientX)
    dragRef.current = {
      pointerId: event.pointerId,
      startPx,
      trackWidth: metrics.width,
      viewEnd,
      viewStart,
    }
    setDragCurrentPx(startPx)
    setDragging(false)
    clearHover()
  }

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current
    if (!drag) return
    const px = pxAtClientX(event.clientX)
    const moved = Math.abs(px - drag.startPx) > DRAG_THRESHOLD_PX
    if (moved && !dragging) setDragging(true)
    if (moved || dragging) {
      setDragCurrentPx(px)
      clearHover()
    }
  }

  // Hover and cue both live on the body so every row — audio included — drives the same readout.
  const onBodyPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current) {
      onPointerMove(event)
      return
    }
    if (playheadDragRef.current) return
    hoverAtClientX(event.clientX)
  }

  const onBodyDoubleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isInteractiveControl(event.target)) return
    const metrics = trackMetrics()
    if (!metrics || event.clientX < metrics.left || event.clientX > metrics.left + metrics.width) return
    onResetZoom?.()
  }

  const finishPointer = (event: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    dragRef.current = null
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    const endPx = pxAtClientX(event.clientX)
    const startPx = drag.startPx
    setDragging(false)
    setDragCurrentPx(null)
    if (cancelled) return
    if (Math.abs(endPx - startPx) > DRAG_THRESHOLD_PX) {
      const fromMs = timeFromDragPx(Math.min(startPx, endPx), drag)
      const toMs = timeFromDragPx(Math.max(startPx, endPx), drag)
      onViewChange(clampViewport(fromMs, Math.max(toMs - fromMs, MIN_TIMELINE_VIEW_MS), timelineStart, timelineEnd))
    } else {
      onPinnedTimeChange(timeFromDragPx(startPx, drag))
    }
  }

  const onPointerLeave = () => {
    if (dragRef.current) return
    clearHover()
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (
      event.defaultPrevented ||
      event.nativeEvent.isComposing ||
      event.metaKey ||
      event.ctrlKey ||
      event.altKey ||
      event.shiftKey
    ) {
      return
    }
    if (onNavigateChunk && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault()
      if (!event.repeat) onNavigateChunk(event.key === 'ArrowLeft' ? -1 : 1)
      return
    }
    if (event.code === 'Space' || event.key === ' ') {
      if (
        event.target !== event.currentTarget ||
        audioLane.state !== 'present' ||
        event.repeat ||
        event.metaKey ||
        event.ctrlKey ||
        event.altKey
      ) {
        return
      }
      event.preventDefault()
      audioLane.onTogglePlayback()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      clearHover()
      onPinnedTimeChange(null)
      return
    }
    const step = Math.max(1, Math.round(duration / 100))
    const current = pinnedTime ?? playheadTime ?? viewStart
    let next: number
    switch (event.key) {
      case 'ArrowLeft':
        next = current - step
        break
      case 'ArrowRight':
        next = current + step
        break
      case 'Home':
        next = viewStart
        break
      case 'End':
        next = viewEnd
        break
      default:
        return
    }
    event.preventDefault()
    clearHover()
    onPinnedTimeChange(clamp(next, viewStart, viewEnd))
  }

  const onPlayheadPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    event.currentTarget.setPointerCapture?.(event.pointerId)
    playheadDragRef.current = { pointerId: event.pointerId }
    setPlayheadDragTime(playheadTime ?? viewStart)
    clearHover()
  }

  const onPlayheadPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (playheadDragRef.current?.pointerId !== event.pointerId) return
    event.stopPropagation()
    setPlayheadDragTime(timeAtClientX(event.clientX))
  }

  const finishPlayheadPointer = (event: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    if (playheadDragRef.current?.pointerId !== event.pointerId) return
    event.stopPropagation()
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    playheadDragRef.current = null
    if (!cancelled) onPinnedTimeChange(timeAtClientX(event.clientX))
    setPlayheadDragTime(null)
  }

  const renderedPlayheadTime = playheadDragTime ?? playheadTime
  const showPlayhead =
    !dragging && renderedPlayheadTime != null && renderedPlayheadTime >= viewStart && renderedPlayheadTime <= viewEnd
  const playheadFrac = renderedPlayheadTime == null ? 0 : (renderedPlayheadTime - viewStart) / duration
  const playheadEdge = playheadFrac <= 0.05 ? 'start' : playheadFrac >= 0.95 ? 'end' : undefined
  const showHover = !dragging && hoverTime != null && hoverTime >= viewStart && hoverTime <= viewEnd
  const hoverFrac = hoverTime == null ? 0 : (hoverTime - viewStart) / duration
  const hoverSnapshots = hoverTime != null ? laneSnapshotsAtTime(visibleLanes, hoverTime, speakerStyles) : []
  const sliderTime = pinnedTime ?? playheadTime ?? viewStart
  const dragStartPx = dragRef.current?.startPx
  const selection =
    dragging && dragStartPx != null && dragCurrentPx != null
      ? {
          left: Math.min(dragStartPx, dragCurrentPx),
          width: Math.abs(dragCurrentPx - dragStartPx),
        }
      : null

  return (
    <div className={styles.swimlane}>
      <div className={styles.axis}>
        <div className={styles.axisLabelGutter} />
        <div className={styles.axisTicks}>
          {ticks.map((t, index) => (
            <span
              key={index}
              className={styles.tick}
              style={{ left: `${((t - viewStart) / duration) * 100}%` }}
              data-edge={index === 0 ? 'start' : index === TICK_COUNT ? 'end' : undefined}
            >
              {formatMs(t)}
            </span>
          ))}
        </div>
      </div>

      <div
        ref={bodyRef}
        className={styles.timelineBody}
        onPointerDown={onPointerDown}
        onPointerMove={onBodyPointerMove}
        onPointerUp={finishPointer}
        onPointerCancel={(event) => finishPointer(event, true)}
        onPointerLeave={onPointerLeave}
        onDoubleClick={onBodyDoubleClick}
      >
        {audioRow}
        <div
          className={styles.lanes}
          onKeyDown={onKeyDown}
          role="slider"
          aria-label="Timeline — hover to inspect, click to cue, drag a region to zoom, pinch to zoom, scroll sideways to pan, press Space to play or pause, or press Escape to clear"
          aria-valuemin={viewStart}
          aria-valuemax={viewEnd}
          aria-valuenow={sliderTime}
          aria-valuetext={pinnedTime == null && playheadTime == null ? 'No cue' : `${formatMs(sliderTime)}, playhead`}
          tabIndex={0}
        >
          {laneRows}
        </div>

        {(showPlayhead || selection || selectedRange) && (
          <div className={styles.playheadLayer} aria-hidden="true">
            {selectedRange && selectedRange.startMs < viewEnd && selectedRange.endMs > viewStart && (
              <div
                className={styles.reviewRange}
                data-testid="selected-review-range"
                style={{
                  left: `${((Math.max(viewStart, selectedRange.startMs) - viewStart) / duration) * 100}%`,
                  width: `${((Math.min(viewEnd, selectedRange.endMs) - Math.max(viewStart, selectedRange.startMs)) / duration) * 100}%`,
                }}
              />
            )}
            {selection && (
              <div
                className={styles.selection}
                data-testid="timeline-selection"
                style={{ left: `${selection.left}px`, width: `${selection.width}px` }}
              />
            )}
            {showPlayhead && renderedPlayheadTime != null && (
              <div className={styles.playhead} style={{ left: `${playheadFrac * 100}%` }} data-edge={playheadEdge}>
                <div className={styles.playheadTimestamp} data-testid="playhead-timestamp">
                  {formatMs(renderedPlayheadTime)}
                </div>
                <div
                  className={styles.playheadHandle}
                  data-testid="playhead-handle"
                  onDoubleClick={(event) => event.stopPropagation()}
                  onPointerDown={onPlayheadPointerDown}
                  onPointerMove={onPlayheadPointerMove}
                  onPointerUp={finishPlayheadPointer}
                  onPointerCancel={(event) => finishPlayheadPointer(event, true)}
                />
              </div>
            )}
          </div>
        )}

        {showHover && (
          <div className={styles.playheadLayer} aria-hidden="true">
            <div
              className={styles.hoverPlayhead}
              style={{ left: `${hoverFrac * 100}%` }}
              data-flip={hoverFrac > 0.62 || undefined}
            >
              <div className={styles.playheadCard} data-testid="playhead-snapshot">
                <div className={styles.playheadCardTime}>{formatMs(hoverTime)}</div>
                {hoverSnapshots.map((snapshot) => (
                  <div key={snapshot.laneLabel} className={styles.playheadCardRow}>
                    <span className={styles.playheadCardLane}>{snapshot.laneLabel}</span>
                    {snapshot.speakers.length === 0 ? (
                      <span className={styles.playheadCardEmpty}>—</span>
                    ) : (
                      <span className={styles.playheadCardSpeakers}>
                        {snapshot.speakers.map((speaker, index) => (
                          <span key={index} className={styles.playheadCardSpeaker}>
                            <span
                              className={styles.playheadCardSwatch}
                              style={{ backgroundColor: speaker.color }}
                              aria-hidden="true"
                            />
                            {speaker.label}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Swimlane
